import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { SceneStore } from '@os/doc';
import { CANVAS_UI } from '../editor/canvas-ui';

/**
 * In-place renaming of a section, over its title pill above its corner (same place and screen size as the pill, P5e).
 * The draft starts from the name at opening: a peer's rename meanwhile never overwrites it.
 */
export function SectionTitleEditor({ store, id, onDone }: { store: SceneStore; id: string; onDone: () => void }) {
  const { t } = useTranslation();
  const [initial] = useState(() => {
    const o = store.getObject(id);
    return o?.type === 'section' ? o.name : '';
  });
  const w = store.graph().world.get(id);
  const o = store.getObject(id);
  if (!w || o?.type !== 'section') return null;
  let done = false;
  const finish = (value: string | null) => {
    if (done) return;
    done = true;
    if (value !== null && value !== initial && !store.readOnly) store.commands.setProps([id], { name: value });
    onDone();
  };
  return (
    <div
      className="absolute left-0 top-0"
      style={{ transform: `translate(${w.x}px, ${w.y}px) rotate(${w.rotation}deg)`, width: w.w }}
      {...{ [CANVAS_UI]: '' }}
      onPointerDown={(e) => e.stopPropagation()}
    >
      <div className="absolute bottom-full left-0 origin-bottom-left pb-1.5" style={{ transform: 'scale(var(--inv-zoom, 1))' }}>
        <input
          autoFocus
          aria-label={t('editor.sectionName')}
          defaultValue={initial}
          maxLength={200}
          onFocus={(e) => e.currentTarget.select()}
          onBlur={(e) => finish(e.currentTarget.value)}
          onKeyDown={(e) => {
            e.stopPropagation();
            if (e.key === 'Enter') finish(e.currentTarget.value);
            else if (e.key === 'Escape') finish(null);
          }}
          className="w-64 rounded-lg border border-accent bg-white px-2.5 py-1 font-sans text-sm font-semibold text-[#141414] outline-none"
          style={{ maxWidth: `calc(${w.w}px / var(--inv-zoom, 1))` }}
        />
      </div>
    </div>
  );
}
